import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Container, Row, Col, Card, Badge, Pagination, Spinner, Alert, Button } from "react-bootstrap";
import { FaStar, FaPhone, FaUser, FaCalendarAlt, FaFileAlt, FaRedo } from "react-icons/fa";
import { toast } from "react-toastify";
import { requestReviewAPI } from "../services/api";

const REVIEW_STATUS_QUERY_KEY = ["reviewInsights"];

const fetchReviewInsights = async ({ page = 1, pageSize = 10 }) => {
  const response = await requestReviewAPI.getReviewInsights({ page, page_size: pageSize });
  return response.data;
};

const ReviewStatusPage = () => {
  const [page, setPage] = useState(1);
  const pageSize = 10;
  const queryClient = useQueryClient();

  const { data, isLoading, isError, error } = useQuery({
    queryKey: [...REVIEW_STATUS_QUERY_KEY, page],
    queryFn: () => fetchReviewInsights({ page, pageSize }),
    keepPreviousData: true,
  });

  const reRequestMutation = useMutation({
    mutationFn: (proposalPk) => requestReviewAPI.initiateReviewCall({
      proposal_pk: proposalPk,
      consent: true
      // job_closeout_details not sent, so backend uses existing
    }),
    onSuccess: () => {
      toast.success("Review call re-requested successfully!");
      queryClient.invalidateQueries(REVIEW_STATUS_QUERY_KEY);
    },
    onError: (error) => {
      toast.error("Failed to re-request review call.");
      console.error(error);
    },
  });

  const reviews = data?.results || [];
  const totalPages = Math.ceil((data?.count || 0) / pageSize);

  const getStatusVariant = (status) => {
    switch (status) {
      case "successful":
        return "success";
      case "needs_attention":
        return "danger";
      case "follow_up":
        return "warning";
      case "unreachable":
        return "secondary";
      default:
        return "info";
    }
  };

  const getCardStyle = (status) => {
    switch (status) {
      case "successful":
        return { borderColor: "#28a745", borderWidth: "2px" };
      case "needs_attention":
        return { borderColor: "#dc3545", borderWidth: "2px" };
      case "follow_up":
        return { borderColor: "#ffc107", borderWidth: "2px" };
      case "unreachable":
        return { borderColor: "#6c757d", borderWidth: "2px" };
      default:
        return { borderColor: "#17a2b8", borderWidth: "2px" };
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString();
  };

  if (isLoading) {
    return (
      <Container className="py-4">
        <div className="text-center">
          <Spinner animation="border" />
          <p className="mt-2">Loading review insights...</p>
        </div>
      </Container>
    );
  }

  if (isError) {
    return (
      <Container className="py-4">
        <Alert variant="danger">
          Error loading review insights: {error?.message || "Unknown error"}
        </Alert>
      </Container>
    );
  }

  return (
    <Container className="py-4">
      <Row className="mb-4">
        <Col>
          <h2 className="d-flex align-items-center">
            <FaStar className="me-2 text-warning" />
            Review Call Status & Insights
          </h2>
          <p className="text-muted">
            Track review requests for proposals and their outcomes. Review calls must be triggered manually.
          </p>
        </Col>
      </Row>

      {reviews.length === 0 ? (
        <Alert variant="info">
          No review requests available yet. Review calls for proposals must be triggered manually from the Proposal Leads page.
        </Alert>
      ) : (
        <>
          <Row>
            {reviews.map((review) => (
              <Col lg={6} xl={4} key={review.proposal_pk} className="mb-4">
                <Card className="h-100 shadow-sm" style={getCardStyle(review.review_call.status)}>
                  <Card.Header className="d-flex justify-content-between align-items-center">
                    <div className="d-flex align-items-center">
                      <FaFileAlt className="me-2 text-primary" />
                      <strong className="small">Proposal {review.proposal_id}</strong>
                    </div>
                    <Badge bg={getStatusVariant(review.review_call.status)}>
                      {review.review_call.status.replace("_", " ").toUpperCase()}
                    </Badge>
                  </Card.Header>
                  <Card.Body>
                    <div className="mb-3">
                      <div className="d-flex align-items-center mb-2">
                        <FaUser className="me-2 text-muted" size={14} />
                        <small className="text-muted">Client:</small>
                        <strong className="ms-1 small">{review.client.name || "N/A"}</strong>
                      </div>
                      <div className="d-flex align-items-center mb-2">
                        <FaPhone className="me-2 text-muted" size={14} />
                        <small className="text-muted">Phone:</small>
                        <span className="ms-1 small">{review.client.phone || "N/A"}</span>
                      </div>
                      <div className="d-flex align-items-center">
                        <FaCalendarAlt className="me-2 text-muted" size={14} />
                        <small className="text-muted">Requested:</small>
                        <span className="ms-1 small">{formatDate(review.review_call.requested_at)}</span>
                      </div>
                    </div>
                    <div>
                      <small className="text-muted">Summary:</small>
                      <p className="mt-1 small">{review.review_call.summary}</p>
                    </div>
                    {review.review_call.status === "unreachable" && (
                      <div className="mt-3">
                        <Button
                          variant="outline-primary"
                          size="sm"
                          onClick={() => reRequestMutation.mutate(review.proposal_pk)}
                          disabled={reRequestMutation.isLoading}
                        >
                          {reRequestMutation.isLoading ? (
                            <Spinner animation="border" size="sm" />
                          ) : (
                            <><FaRedo className="me-1" /> Request Again</>
                          )}
                        </Button>
                      </div>
                    )}
                  </Card.Body>
                </Card>
              </Col>
            ))}
          </Row>

          {totalPages > 1 && (
            <Row className="mt-4">
              <Col className="d-flex justify-content-center">
                <Pagination>
                  <Pagination.First
                    disabled={page === 1}
                    onClick={() => setPage(1)}
                  />
                  <Pagination.Prev
                    disabled={page === 1}
                    onClick={() => setPage(page - 1)}
                  />
                  {[...Array(totalPages)].map((_, index) => (
                    <Pagination.Item
                      key={index + 1}
                      active={index + 1 === page}
                      onClick={() => setPage(index + 1)}
                    >
                      {index + 1}
                    </Pagination.Item>
                  ))}
                  <Pagination.Next
                    disabled={page === totalPages}
                    onClick={() => setPage(page + 1)}
                  />
                  <Pagination.Last
                    disabled={page === totalPages}
                    onClick={() => setPage(totalPages)}
                  />
                </Pagination>
              </Col>
            </Row>
          )}
        </>
      )}
    </Container>
  );
};

export default ReviewStatusPage;